// CB:BEGIN admin-project-detail-page
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@clerk/clerk-react';
// CB:BEGIN priority2-project-management-import
import { ProjectManagementActions } from '../../components/admin/ProjectManagementActions';
// CB:END priority2-project-management-import
// CB:BEGIN help-support-ops-import
import HelpSupportOpsPanel from '../../components/help/HelpSupportOpsPanel';
// CB:END help-support-ops-import
// CB:BEGIN chat-integration-admin
import ChatPanel from '../../components/chat/ChatPanel';
// CB:END chat-integration-admin
// CB:BEGIN admin-project-timeline-import
import ProjectTimeline from '../../components/ProjectTimeline';
// CB:END admin-project-timeline-import
// CB:BEGIN admin-project-impersonation-import
import { useImpersonation } from '../../context/ImpersonationContext';
// CB:END admin-project-impersonation-import

// CB:BEGIN phase6-realtor-origin-type
interface RealtorOrigin {
  quoteRequestId: string | null;
  quoteRequest?: { id: string; status: string; realtor?: { id: string; email: string; realtorProfile?: { brokerageName?: string | null } | null } | null } | null;
  invite?: { id: string; status: string; homeownerEmail?: string | null; sentAt?: string | null; openedAt?: string | null; claimedAt?: string | null } | null;
  attribution?: { id: string; sourceType: string; isWinner: boolean } | null;
}
// CB:END phase6-realtor-origin-type

interface Project {
  id: string;
  address: string;
  status: string;
  requestorType?: string;
  originQuoteRequestId?: string | null;
  realtorOrigin?: RealtorOrigin | null;
  createdAt: string;
  updatedAt?: string;
  statusEnteredAt?: string | null;
  stalledFlag?: boolean;
  stalledAt?: string | null;
  lastNextActionCode?: string | null;
  lastNextActionOwner?: string | null;
  lastNextActionAt?: string | null;
  reminderCount?: number;
  homeowner?: {
    id?: string;
    userId?: string;
    firstName?: string | null;
    lastName?: string | null;
    user: {
      email: string;
    };
  };
  acceptedContractorUserId?: string | null;
  contractor?: {
    user: {
      email: string;
    };
  };
  bids: Array<{
    id: string;
    amountCents?: number;
    isAccepted: boolean;
    contractor?: {
      user: {
        email: string;
      };
    };
  }>;
  // CB:BEGIN admin-roof-metrics-type
  roofMetrics?: {
    totalAreaSqFt?: number | null;
    predominantPitch?: string | null;
    ridgesFt?: number | null;
    hipsFt?: number | null;
    valleysFt?: number | null;
    rakesFt?: number | null;
    eavesStarterFt?: number | null;
    dripEdgeFt?: number | null;
    totalRoofFacets?: number | null;
    source?: string | null;
    extractedAt?: string | null;
  } | null;
  // CB:END admin-roof-metrics-type
}

export default function AdminProjectDetailPage() {
  const { id } = useParams();
  const { getToken, userId: adminClerkId } = useAuth();
  const { enterSession } = useImpersonation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [contractorId, setContractorId] = useState('');
  const [activeTab, setActiveTab] = useState<'controls' | 'messages' | 'activity'>('controls');
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  const loadProject = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await fetch(`/api/admin/projects/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setProject(data.project);
      setStatus(data.project.status);
    } catch (e: any) {
      console.error('Error loading project:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
  }, [id, getToken]);

  const saveStatus = async () => {
    if (!id) return;
    
    try {
      const token = await getToken();
      
      const response = await fetch(`/api/admin/projects/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Reload project to reflect changes
      loadProject();
    } catch (e: any) {
      console.error('Error updating project status:', e);
      alert(`Error: ${e.message}`);
    }
  };

  const assignContractor = async () => {
    if (!id || !contractorId) return;
    
    try {
      const token = await getToken();
      
      const response = await fetch(`/api/admin/projects/${id}/assign-contractor`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ contractorId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Reload project to reflect changes
      loadProject();
      setContractorId('');
    } catch (e: any) {
      console.error('Error assigning contractor:', e);
      alert(`Error: ${e.message}`);
    }
  };

  const acceptBid = async (bidId: string) => {
    if (!id) return;

    try {
      const token = await getToken();

      const response = await fetch(`/api/admin/projects/${id}/accept-bid`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ bidId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Reload project to reflect changes
      loadProject();
    } catch (e: any) {
      console.error('Error accepting bid:', e);
      alert(`Error: ${e.message}`);
    }
  };

  // CB:BEGIN admin-project-impersonation
  const handleImpersonate = async (targetUserId: string, role: string) => {
    setImpersonating(role);
    setImpersonateError(null);
    try {
      await enterSession(targetUserId);
      // Banner appears automatically; admin can now navigate to any user-facing page
    } catch (e: any) {
      setImpersonateError(e.message || 'Failed to start session');
    } finally {
      setImpersonating(null);
    }
  };
  // CB:END admin-project-impersonation

  if (loading) {
    return (
      <AdminLayout title="Project Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading project...</span>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Project Details">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600 text-sm">Error loading project: {error}</p>
          <button 
            onClick={loadProject}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  if (!project) {
    return (
      <AdminLayout title="Project Details">
        <div className="text-center py-12">
          <p className="text-gray-500">Project not found</p>
        </div>
      </AdminLayout>
    );
  }

  const TAB_STYLE = (active: boolean) => ({
    padding: '8px 18px',
    fontSize: 14,
    fontWeight: 600,
    border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    background: 'none',
    color: active ? '#2563eb' : '#6b7280',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  });

  // CB:BEGIN admin-stat-field-helper
  const StatField = ({ label, value, mono = false, warn = false }: { label: string; value: string; mono?: boolean; warn?: boolean }) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit', color: warn ? '#dc2626' : '#111827', fontWeight: warn ? 700 : 400 }}>{value}</div>
    </div>
  );
  // CB:END admin-stat-field-helper

  return (
    <AdminLayout title={`Project: ${project.address}`}>
      <div className="space-y-6">
        {/* CB:BEGIN phase6-realtor-origin-banner */}
        {project.requestorType === 'REALTOR_PORTAL' && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🏡</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>Realtor-Assisted Origin</div>
                <div style={{ fontSize: 13, color: '#15803d', marginTop: 2 }}>
                  {project.realtorOrigin?.quoteRequest?.realtor?.email
                    ? <>Referred by <strong>{project.realtorOrigin.quoteRequest.realtor.email}</strong>{project.realtorOrigin.quoteRequest.realtor.realtorProfile?.brokerageName ? ` · ${project.realtorOrigin.quoteRequest.realtor.realtorProfile.brokerageName}` : ''}</>
                    : 'Created from a realtor portal claim.'}
                </div>
                {project.realtorOrigin?.invite && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                    Invite: <span style={{ fontFamily: 'monospace', background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>{project.realtorOrigin.invite.status}</span>
                    {project.realtorOrigin.invite.claimedAt && <> · Claimed {new Date(project.realtorOrigin.invite.claimedAt).toLocaleDateString()}</>}
                  </div>
                )}
              </div>
            </div>
            {project.originQuoteRequestId && (
              <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace', background: '#f1f5f9', padding: '3px 8px', borderRadius: 4 }}>
                qr:{project.originQuoteRequestId.slice(0, 12)}…
              </span>
            )}
          </div>
        )}
        {/* CB:END phase6-realtor-origin-banner */}

        {/* CB:BEGIN admin-project-info-panel */}
        {/* Project Info — always visible */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Project Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <p className="mt-1 text-sm text-gray-900">{project.address}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <p className="mt-1 text-sm text-gray-900">
                <span style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{project.status}</span>
                {project.requestorType && project.requestorType !== 'HOMEOWNER' && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, background: '#ede9fe', color: '#5b21b6', padding: '2px 7px', borderRadius: 4 }}>{project.requestorType}</span>
                )}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Created</label>
              <p className="mt-1 text-sm text-gray-900">{new Date(project.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Homeowner</label>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-900">{project.homeowner?.user?.email || 'No homeowner assigned'}</span>
                {project.homeowner?.userId && (
                  <button
                    onClick={() => handleImpersonate(project.homeowner!.userId!, 'homeowner')}
                    disabled={!!impersonating}
                    style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, border: '1px solid #d97706', background: '#fffbeb', color: '#92400e', cursor: 'pointer' }}
                  >
                    {impersonating === 'homeowner' ? 'Opening…' : 'Open as User'}
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contractor (accepted bid)</label>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-900">
                  {project.bids?.find(b => b.isAccepted)?.contractor?.user?.email || 'No contractor assigned'}
                </span>
                {project.acceptedContractorUserId && (
                  <button
                    onClick={() => handleImpersonate(project.acceptedContractorUserId!, 'contractor')}
                    disabled={!!impersonating}
                    style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, border: '1px solid #d97706', background: '#fffbeb', color: '#92400e', cursor: 'pointer' }}
                  >
                    {impersonating === 'contractor' ? 'Opening…' : 'Open as User'}
                  </button>
                )}
              </div>
            </div>
          </div>
          {impersonateError && (
            <p style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>Impersonation failed: {impersonateError}</p>
          )}
        </div>

        {/* CB:BEGIN admin-project-state-debug */}
        {/* State Machine Debug — always visible */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>State Machine</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            <StatField label="Status" value={project.status} mono />
            <StatField label="Time in State" value={project.statusEnteredAt ? `${Math.floor((Date.now() - new Date(project.statusEnteredAt).getTime()) / 86400000)}d (since ${new Date(project.statusEnteredAt).toLocaleDateString()})` : '—'} />
            <StatField label="Stalled" value={project.stalledFlag ? `Yes — since ${project.stalledAt ? new Date(project.stalledAt).toLocaleDateString() : '?'}` : 'No'} warn={!!project.stalledFlag} />
            <StatField label="Next Action" value={project.lastNextActionCode ?? '—'} mono />
            <StatField label="Next Action Owner" value={project.lastNextActionOwner ?? '—'} />
            <StatField label="Reminder Count" value={String(project.reminderCount ?? 0)} warn={(project.reminderCount ?? 0) >= 2} />
          </div>
        </div>
        {/* CB:END admin-project-state-debug */}
        {/* CB:END admin-project-info-panel */}

        {/* CB:BEGIN admin-tab-bar */}
        <div style={{ borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 4 }}>
          <button style={TAB_STYLE(activeTab === 'controls')} onClick={() => setActiveTab('controls')}>
            Admin Controls
          </button>
          <button style={TAB_STYLE(activeTab === 'messages')} onClick={() => setActiveTab('messages')}>
            Messages
            {activeTab !== 'messages' && (
              <span style={{ marginLeft: 6, fontSize: 11, background: '#dbeafe', color: '#1d4ed8', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>
                View
              </span>
            )}
          </button>
          {/* CB:BEGIN admin-activity-tab-btn */}
          <button style={TAB_STYLE(activeTab === 'activity')} onClick={() => setActiveTab('activity')}>
            Activity
          </button>
          {/* CB:END admin-activity-tab-btn */}
        </div>
        {/* CB:END admin-tab-bar */}

        {/* CB:BEGIN admin-controls-tab */}
        {activeTab === 'controls' && (
          <>
            {/* CB:BEGIN help-support-ops-panel */}
            <HelpSupportOpsPanel
              defaultRole="homeowner"
              contextLabel={`Project ${project.id}`}
              recipientName={project.homeowner?.user?.email || 'there'}
            />
            {/* CB:END help-support-ops-panel */}

            {/* CB:BEGIN priority2-project-management-component */}
            <ProjectManagementActions
              projectId={project.id}
              projectAddress={project.address}
              currentStatus={project.status}
              onSuccess={loadProject}
            />
            {/* CB:END priority2-project-management-component */}

            {/* Status Management */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Status Management</h2>
              <div className="flex items-center gap-4">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                </select>
                <button onClick={saveStatus} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                  Update Status
                </button>
              </div>
            </div>

            {/* Contractor Assignment */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Assign Contractor</h2>
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  value={contractorId}
                  onChange={(e) => setContractorId(e.target.value)}
                  placeholder="Enter contractor ID..."
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={assignContractor}
                  disabled={!contractorId}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Assign Contractor
                </button>
              </div>
            </div>

            {/* CB:BEGIN admin-roof-metrics-panel */}
            {project.roofMetrics && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Roof Measurements</h2>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                  Source: {project.roofMetrics.source ?? 'EagleView PDF'}{project.roofMetrics.extractedAt ? ` · Extracted ${new Date(project.roofMetrics.extractedAt).toLocaleDateString()}` : ''}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                  {project.roofMetrics.totalAreaSqFt != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Total Area</div><div style={{ fontSize: 14, fontWeight: 600 }}>{project.roofMetrics.totalAreaSqFt.toLocaleString()} sq ft</div></div>
                  )}
                  {project.roofMetrics.predominantPitch && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Pitch</div><div style={{ fontSize: 14 }}>{project.roofMetrics.predominantPitch}</div></div>
                  )}
                  {project.roofMetrics.totalRoofFacets != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Facets</div><div style={{ fontSize: 14 }}>{project.roofMetrics.totalRoofFacets}</div></div>
                  )}
                  {project.roofMetrics.ridgesFt != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Ridges</div><div style={{ fontSize: 14 }}>{project.roofMetrics.ridgesFt} ft</div></div>
                  )}
                  {project.roofMetrics.hipsFt != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Hips</div><div style={{ fontSize: 14 }}>{project.roofMetrics.hipsFt} ft</div></div>
                  )}
                  {project.roofMetrics.valleysFt != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Valleys</div><div style={{ fontSize: 14 }}>{project.roofMetrics.valleysFt} ft</div></div>
                  )}
                  {project.roofMetrics.rakesFt != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Rakes</div><div style={{ fontSize: 14 }}>{project.roofMetrics.rakesFt} ft</div></div>
                  )}
                  {project.roofMetrics.eavesStarterFt != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Eaves</div><div style={{ fontSize: 14 }}>{project.roofMetrics.eavesStarterFt} ft</div></div>
                  )}
                  {project.roofMetrics.dripEdgeFt != null && (
                    <div><div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Drip Edge</div><div style={{ fontSize: 14 }}>{project.roofMetrics.dripEdgeFt} ft</div></div>
                  )}
                </div>
              </div>
            )}
            {/* CB:END admin-roof-metrics-panel */}

            {/* Bids Management */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Bids</h2>
              {project.bids.length > 0 ? (
                <div className="space-y-3">
                  {project.bids.map((bid) => (
                    <div key={bid.id} className="flex items-center justify-between border rounded-lg p-4">
                      <div>
                        <div className="font-medium">${bid.amountCents ? (bid.amountCents / 100).toFixed(2) : 'N/A'}</div>
                        <div className="text-sm text-gray-500">Contractor: {bid.contractor?.user?.email || 'Unknown'}</div>
                        <div className="text-xs text-gray-400">Status: {bid.isAccepted ? 'ACCEPTED' : 'PENDING'}</div>
                      </div>
                      {!bid.isAccepted && (
                        <button onClick={() => acceptBid(bid.id)} className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm">
                          Accept Bid
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No bids received</p>
              )}
            </div>
          </>
        )}
        {/* CB:END admin-controls-tab */}

        {/* CB:BEGIN admin-activity-tab */}
        {activeTab === 'activity' && (
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Project Activity Timeline</h2>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Full audit trail of project events — status transitions, bids, milestones, payments, disputes.
            </p>
            <ProjectTimeline projectId={project.id} defaultCollapsed={false} />
          </div>
        )}
        {/* CB:END admin-activity-tab */}

        {/* CB:BEGIN admin-messages-tab — FR-CHAT-006 admin oversight */}
        {activeTab === 'messages' && adminClerkId && (
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Read-only view of all messages between the homeowner and contractor on this project. Admins cannot send messages.
              </p>
            </div>
            <ChatPanel
              projectId={project.id}
              currentUserId={adminClerkId}
              currentUserRole="ADMIN"
              projectStatus={project.status}
              isPostSelection={project.bids?.some((b) => b.isAccepted)}
            />
          </div>
        )}
        {/* CB:END admin-messages-tab */}
      </div>
    </AdminLayout>
  );
}
// CB:END admin-project-detail-page
